import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';

// ── US State Sales Tax Rates ──────────────────────────────────────────
const US_STATE_TAX = {
  AL: { name: 'Alabama', rate: 0.04 }, AK: { name: 'Alaska', rate: 0.00 },
  AZ: { name: 'Arizona', rate: 0.056 }, AR: { name: 'Arkansas', rate: 0.065 },
  CA: { name: 'California', rate: 0.0725 }, CO: { name: 'Colorado', rate: 0.029 },
  CT: { name: 'Connecticut', rate: 0.0635 }, DE: { name: 'Delaware', rate: 0.00 },
  FL: { name: 'Florida', rate: 0.06 }, GA: { name: 'Georgia', rate: 0.04 },
  HI: { name: 'Hawaii', rate: 0.04 }, ID: { name: 'Idaho', rate: 0.06 },
  IL: { name: 'Illinois', rate: 0.0625 }, IN: { name: 'Indiana', rate: 0.07 },
  IA: { name: 'Iowa', rate: 0.06 }, KS: { name: 'Kansas', rate: 0.065 },
  KY: { name: 'Kentucky', rate: 0.06 }, LA: { name: 'Louisiana', rate: 0.0445 },
  ME: { name: 'Maine', rate: 0.055 }, MD: { name: 'Maryland', rate: 0.06 },
  MA: { name: 'Massachusetts', rate: 0.0625 }, MI: { name: 'Michigan', rate: 0.06 },
  MN: { name: 'Minnesota', rate: 0.06875 }, MS: { name: 'Mississippi', rate: 0.07 },
  MO: { name: 'Missouri', rate: 0.04225 }, MT: { name: 'Montana', rate: 0.00 },
  NE: { name: 'Nebraska', rate: 0.055 }, NV: { name: 'Nevada', rate: 0.0685 },
  NH: { name: 'New Hampshire', rate: 0.00 }, NJ: { name: 'New Jersey', rate: 0.06625 },
  NM: { name: 'New Mexico', rate: 0.05125 }, NY: { name: 'New York', rate: 0.04 },
  NC: { name: 'North Carolina', rate: 0.0475 }, ND: { name: 'North Dakota', rate: 0.05 },
  OH: { name: 'Ohio', rate: 0.0575 }, OK: { name: 'Oklahoma', rate: 0.045 },
  OR: { name: 'Oregon', rate: 0.00 }, PA: { name: 'Pennsylvania', rate: 0.06 },
  RI: { name: 'Rhode Island', rate: 0.07 }, SC: { name: 'South Carolina', rate: 0.06 },
  SD: { name: 'South Dakota', rate: 0.042 }, TN: { name: 'Tennessee', rate: 0.07 },
  TX: { name: 'Texas', rate: 0.0625 }, UT: { name: 'Utah', rate: 0.061 },
  VT: { name: 'Vermont', rate: 0.06 }, VA: { name: 'Virginia', rate: 0.053 },
  WA: { name: 'Washington', rate: 0.065 }, WV: { name: 'West Virginia', rate: 0.06 },
  WI: { name: 'Wisconsin', rate: 0.05 }, WY: { name: 'Wyoming', rate: 0.04 },
  DC: { name: 'Washington DC', rate: 0.06 },
};

function detectTaxRate(city = '') {
  const upper = city.toUpperCase();
  for (const [abbr, info] of Object.entries(US_STATE_TAX)) {
    const patterns = [`, ${abbr},`, `, ${abbr}`, ` ${abbr} `, ` ${abbr},`];
    if (patterns.some(p => upper.includes(p)) || upper.trim() === abbr) {
      return { rate: info.rate, state: info.name, abbr };
    }
  }
  return { rate: 0, state: '', abbr: '' };
}

const LANGUAGES = [
  { code: 'en', label: 'English',                          native: 'English'          },
  { code: 'es', label: 'Spanish',                          native: 'Español'          },
  { code: 'zh', label: 'Chinese (Simplified)',             native: '中文(简体)'          },
  { code: 'tl', label: 'Filipino',                         native: 'Filipino'         },
  { code: 'vi', label: 'Vietnamese',                       native: 'Tiếng Việt'       },
  { code: 'ar', label: 'Arabic',                           native: 'العربية'          },
  { code: 'fr', label: 'French',                           native: 'Français'         },
  { code: 'ko', label: 'Korean',                           native: '한국어'              },
  { code: 'ru', label: 'Russian',                          native: 'Русский'          },
  { code: 'hi', label: 'Hindi',                            native: 'हिंदी'              },
  { code: 'ja', label: 'Japanese',                         native: '日本語'              },
  { code: 'pt', label: 'Portuguese',                       native: 'Português'        },
  { code: 'de', label: 'German',                           native: 'Deutsch'          },
  { code: 'th', label: 'Thai',                             native: 'ภาษาไทย'           },
  { code: 'it', label: 'Italian',                          native: 'Italiano'         },
];

const UI = {
  en: { search: 'Search menu items...', all: 'All', noItems: 'No items found', onlineOrder: '🛒 Online Order', onlineOrderOn: '✅ Online Order ON', openNow: 'Open Now', closedNow: 'Closed Now', veg: 'Veg', nonveg: 'Non Veg', vegan: 'Vegan', halal: 'Halal', addToCart: '+ Add', viewCart: '🛒 View Cart', checkout: 'Proceed to Checkout →', total: 'Total', translating: 'Translating...' },
  es: { search: 'Buscar en el menú...', all: 'Todo', noItems: 'No hay artículos', onlineOrder: '🛒 Pedir en línea', onlineOrderOn: '✅ Pedido activo', openNow: 'Abierto', closedNow: 'Cerrado', veg: 'Vegetal', nonveg: 'No vegetal', vegan: 'Vegano', halal: 'Halal', addToCart: '+ Añadir', viewCart: '🛒 Ver carrito', checkout: 'Pagar →', total: 'Total', translating: 'Traduciendo...' },
  zh: { search: '搜索菜单...', all: '全部', noItems: '未找到项目', onlineOrder: '🛒 在线订购', onlineOrderOn: '✅ 订单已开启', openNow: '营业中', closedNow: '已关闭', veg: '素食', nonveg: '非素食', vegan: '全素', halal: '清真', addToCart: '+ 添加', viewCart: '🛒 查看购物车', checkout: '去结账 →', total: '总计', translating: '翻译中...' },
  tl: { search: 'Maghanap ng pagkain...', all: 'Lahat', noItems: 'Walang item', onlineOrder: '🛒 Mag-order online', onlineOrderOn: '✅ Naka-order na', openNow: 'Bukas', closedNow: 'Sarado', veg: 'Gulay', nonveg: 'May Karne', vegan: 'Vegan', halal: 'Halal', addToCart: '+ Idagdag', viewCart: '🛒 Tingnan ang Cart', checkout: 'Magbayad →', total: 'Kabuuan', translating: 'Isinasalin...' },
  vi: { search: 'Tìm kiếm món ăn...', all: 'Tất cả', noItems: 'Không tìm thấy', onlineOrder: '🛒 Đặt hàng online', onlineOrderOn: '✅ Đang đặt hàng', openNow: 'Đang mở', closedNow: 'Đã đóng', veg: 'Chay', nonveg: 'Không chay', vegan: 'Thuần chay', halal: 'Halal', addToCart: '+ Thêm', viewCart: '🛒 Xem giỏ hàng', checkout: 'Thanh toán →', total: 'Tổng cộng', translating: 'Đang dịch...' },
  ar: { search: 'البحث في القائمة...', all: 'الكل', noItems: 'لا توجد عناصر', onlineOrder: '🛒 اطلب أونلاين', onlineOrderOn: '✅ الطلب نشط', openNow: 'مفتوح', closedNow: 'مغلق', veg: 'نباتي', nonveg: 'غير نباتي', vegan: 'نباتي صرف', halal: 'حلال', addToCart: '+ أضف', viewCart: '🛒 عرض السلة', checkout: 'الدفع →', total: 'المجموع', translating: 'جارٍ الترجمة...' },
  fr: { search: 'Rechercher...', all: 'Tout', noItems: 'Aucun article', onlineOrder: '🛒 Commander', onlineOrderOn: '✅ Commande active', openNow: 'Ouvert', closedNow: 'Fermé', veg: 'Végétarien', nonveg: 'Non végétarien', vegan: 'Végétalien', halal: 'Halal', addToCart: '+ Ajouter', viewCart: '🛒 Voir panier', checkout: 'Commander →', total: 'Total', translating: 'Traduction...' },
  ko: { search: '메뉴 검색...', all: '전체', noItems: '항목 없음', onlineOrder: '🛒 온라인 주문', onlineOrderOn: '✅ 주문 활성화', openNow: '영업 중', closedNow: '영업 종료', veg: '채식', nonveg: '비채식', vegan: '비건', halal: '할랄', addToCart: '+ 추가', viewCart: '🛒 장바구니', checkout: '결제하기 →', total: '합계', translating: '번역 중...' },
  ru: { search: 'Поиск блюд...', all: 'Все', noItems: 'Ничего не найдено', onlineOrder: '🛒 Заказать онлайн', onlineOrderOn: '✅ Заказ активен', openNow: 'Открыто', closedNow: 'Закрыто', veg: 'Вегетарианское', nonveg: 'Мясное', vegan: 'Веганское', halal: 'Халяль', addToCart: '+ Добавить', viewCart: '🛒 Корзина', checkout: 'Оформить →', total: 'Итого', translating: 'Перевод...' },
  hi: { search: 'मेनू खोजें...', all: 'सभी', noItems: 'कोई आइटम नहीं', onlineOrder: '🛒 ऑनलाइन ऑर्डर', onlineOrderOn: '✅ ऑर्डर चालू', openNow: 'अभी खुला', closedNow: 'अभी बंद', veg: 'शाकाहारी', nonveg: 'मांसाहारी', vegan: 'वीगन', halal: 'हलाल', addToCart: '+ जोड़ें', viewCart: '🛒 कार्ट देखें', checkout: 'चेकआउट →', total: 'कुल', translating: 'अनुवाद हो रहा है...' },
  ja: { search: 'メニューを検索...', all: 'すべて', noItems: '商品なし', onlineOrder: '🛒 オンライン注文', onlineOrderOn: '✅ 注文受付中', openNow: '営業中', closedNow: '閉店中', veg: '菜食', nonveg: '肉あり', vegan: 'ヴィーガン', halal: 'ハラール', addToCart: '+ 追加', viewCart: '🛒 カート', checkout: 'レジへ →', total: '合計', translating: '翻訳中...' },
  pt: { search: 'Pesquisar cardápio...', all: 'Todos', noItems: 'Nenhum item', onlineOrder: '🛒 Pedir online', onlineOrderOn: '✅ Pedido ativo', openNow: 'Aberto', closedNow: 'Fechado', veg: 'Vegetariano', nonveg: 'Não veg', vegan: 'Vegano', halal: 'Halal', addToCart: '+ Adicionar', viewCart: '🛒 Carrinho', checkout: 'Finalizar →', total: 'Total', translating: 'Traduzindo...' },
  de: { search: 'Menü durchsuchen...', all: 'Alle', noItems: 'Keine Artikel', onlineOrder: '🛒 Online bestellen', onlineOrderOn: '✅ Bestellung aktiv', openNow: 'Geöffnet', closedNow: 'Geschlossen', veg: 'Vegetarisch', nonveg: 'Nicht veg', vegan: 'Vegan', halal: 'Halal', addToCart: '+ Hinzufügen', viewCart: '🛒 Warenkorb', checkout: 'Zur Kasse →', total: 'Gesamt', translating: 'Übersetze...' },
  th: { search: 'ค้นหาเมนู...', all: 'ทั้งหมด', noItems: 'ไม่พบรายการ', onlineOrder: '🛒 สั่งออนไลน์', onlineOrderOn: '✅ กำลังสั่ง', openNow: 'เปิดอยู่', closedNow: 'ปิดแล้ว', veg: 'มังสวิรัติ', nonveg: 'มีเนื้อสัตว์', vegan: 'วีแกน', halal: 'ฮาลาล', addToCart: '+ เพิ่ม', viewCart: '🛒 ดูตะกร้า', checkout: 'ชำระเงิน →', total: 'รวม', translating: 'กำลังแปล...' },
  it: { search: 'Cerca nel menu...', all: 'Tutto', noItems: 'Nessun articolo', onlineOrder: '🛒 Ordina online', onlineOrderOn: '✅ Ordine attivo', openNow: 'Aperto', closedNow: 'Chiuso', veg: 'Vegetariano', nonveg: 'Non vegetariano', vegan: 'Vegano', halal: 'Halal', addToCart: '+ Aggiungi', viewCart: '🛒 Carrello', checkout: 'Checkout →', total: 'Totale', translating: 'Traduzione...' },
};

async function translateOne(text, targetLang) {
  if (!text || !text.trim()) return text;
  const codeMap = { zh: 'zh-CN', tl: 'tl' };
  const tl = codeMap[targetLang] || targetLang;
  const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${tl}&dt=t&q=${encodeURIComponent(text)}`;
  try {
    const res = await fetch(url);
    const data = await res.json();
    const translated = data?.[0]?.map(chunk => chunk?.[0] || '').join('') || text;
    return translated;
  } catch {
    return text;
  }
}

async function translateBatch(texts, targetLang) {
  if (targetLang === 'en') return texts;
  const BATCH = 8;
  const results = new Array(texts.length);
  for (let i = 0; i < texts.length; i += BATCH) {
    const batch = texts.slice(i, i + BATCH);
    const translated = await Promise.all(batch.map(t => translateOne(t, targetLang)));
    translated.forEach((t, j) => { results[i + j] = t; });
    if (i + BATCH < texts.length) await new Promise(r => setTimeout(r, 100));
  }
  return results;
}

// ── Gallery Lightbox ─────────────────────────────────────────────────
function GalleryLightbox({ images, startIndex, onClose }) {
  const [current, setCurrent] = useState(startIndex);
  const prev = useCallback(() => setCurrent(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setCurrent(i => (i + 1) % images.length), [images.length]);
  useEffect(() => {
    const fn = (e) => { if (e.key === 'ArrowLeft') prev(); if (e.key === 'ArrowRight') next(); if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [prev, next, onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', fontSize: 28, cursor: 'pointer', width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
      <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: 'white', fontSize: 14, background: 'rgba(255,255,255,0.15)', padding: '4px 14px', borderRadius: 20 }}>{current + 1} / {images.length}</div>
      {images.length > 1 && <button onClick={e => { e.stopPropagation(); prev(); }} style={{ position: 'absolute', left: 16, background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', fontSize: 28, cursor: 'pointer', width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>}
      <img src={images[current].image_url} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: '88vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }} />
      {images.length > 1 && <button onClick={e => { e.stopPropagation(); next(); }} style={{ position: 'absolute', right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', fontSize: 28, cursor: 'pointer', width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>}
      {images.length > 1 && (
        <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.5)', borderRadius: 12, maxWidth: '90vw', overflowX: 'auto' }}>
          {images.map((img, i) => <img key={img.image_id} src={img.image_url} alt="" onClick={e => { e.stopPropagation(); setCurrent(i); }} style={{ width: 52, height: 40, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', flexShrink: 0, border: i === current ? '2px solid white' : '2px solid transparent', opacity: i === current ? 1 : 0.6 }} />)}
        </div>
      )}
    </div>
  );
}

// ── Item Lightbox ────────────────────────────────────────────────────
function ItemLightbox({ item, onClose, onAddToCart }) {
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 20, overflow: 'hidden', maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ position: 'relative' }}>
          <img src={item.image_url} alt={item.name} style={{ width: '100%', height: 260, objectFit: 'cover', display: 'block' }} />
          <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.5)', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          <div style={{ position: 'absolute', bottom: 12, left: 12 }}>
           {item.dietary_type && item.dietary_type !== '' && item.dietary_type !== 'veg-vegan' ? (
          <span className={`badge badge-${item.dietary_type === 'veg' ? 'veg' : item.dietary_type === 'vegan' ? 'vegan' : 'non-veg'}`}>{item.dietary_type}</span>
          ) : null}
          </div>
        </div>
        <div style={{ padding: '20px 24px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, flex: 1, paddingRight: 12 }}>{item.name}</h2>
            <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', flexShrink: 0 }}>${Number(item.price).toFixed(2)}</span>
          </div>
          {item.description && <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: '0 0 16px' }}>{item.description}</p>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {(item.spice_max || item.spice_level || 0) > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ display: 'flex', gap: 2 }}>
                  {Array.from({ length: item.spice_max || item.spice_level || 0 }, (_, i) => {
                    const level = i + 1;
                    const inRange = level >= (item.spice_min || 0) && level <= (item.spice_max || item.spice_level || 0);
                    const hues = ['#fbbf24','#f59e0b','#f97316','#ef4444','#dc2626','#b91c1c','#7f1d1d'];
                    return <div key={i} style={{ width: 10, height: 10, borderRadius: 2, background: inRange ? (hues[i] || '#991b1b') : '#e5e5e5' }} />;
                  })}
                </div>
                <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>
                  {item.spice_min > 0 && item.spice_min !== item.spice_max ? `${item.spice_min}–${item.spice_max}` : `${item.spice_max || item.spice_level}`}
                </span>
              </div>
            )}
            {item.portion_size && (
              <span style={{ fontSize: 12, fontWeight: 700, color: '#555', background: '#f3f4f6', padding: '3px 8px', borderRadius: 6 }}>{item.portion_size}</span>
            )}
            {item.serves && (
              <span style={{ fontSize: 12, fontWeight: 700, color: '#555', background: '#f3f4f6', padding: '3px 8px', borderRadius: 6 }}>{item.serves}</span>
            )}
          </div>
          <button onClick={() => { onAddToCart(item); onClose(); }} style={{ width: '100%', padding: '12px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
            🛒 Add to Cart — ${Number(item.price).toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Cart Sidebar ─────────────────────────────────────────────────────
function CartSidebar({ cart, onClose, onUpdateQty, onRemove, onCheckout, restaurantName, taxRate, taxState }) {
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex' }}>
      <div onClick={onClose} style={{ flex: 1, background: 'rgba(0,0,0,0.5)' }} />
      <div style={{ width: 380, maxWidth: '100vw', background: 'white', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)' }}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>🛒 Your Cart</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#666' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {cart.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🛒</div>
              <p>Your cart is empty</p>
              <p style={{ fontSize: 13 }}>Add items from the menu!</p>
            </div>
          ) : (
            cart.map(item => (
              <div key={item.item_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                {item.image_url && <img src={item.image_url} alt={item.name} style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{item.name}</div>
                  <div style={{ color: 'var(--primary)', fontWeight: 700, fontSize: 14 }}>${Number(item.price).toFixed(2)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <button onClick={() => onUpdateQty(item.item_id, item.qty - 1)} style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--border)', background: 'white', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{item.qty}</span>
                  <button onClick={() => onUpdateQty(item.item_id, item.qty + 1)} style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'var(--primary)', color: 'white', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, minWidth: 56, textAlign: 'right' }}>${(item.price * item.qty).toFixed(2)}</div>
              </div>
            ))
          )}
        </div>
        {cart.length > 0 && (
          <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 15, color: '#555' }}>
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>ℹ️</span>
              <span>Taxes & fees will be calculated by the restaurant at the time of pickup based on your local tax rate.</span>
            </div>
            <div style={{ borderTop: '1px solid var(--border)', marginBottom: 12 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 18, fontWeight: 700 }}>
              <span>Total (before tax)</span>
              <span style={{ color: 'var(--primary)' }}>${subtotal.toFixed(2)}</span>
            </div>
            <button onClick={onCheckout} style={{ width: '100%', padding: '14px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', marginTop: 12 }}>
              Proceed to Checkout →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Checkout Modal ───────────────────────────────────────────────────
function CheckoutModal({ cart, slug, orderType = 'online', onClose, onSuccess, taxRate, taxState }) {
  const [form, setForm] = useState({ customer_name: '', customer_phone: '', customer_email: '', notes: '', table_number: '' });
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const taxAmount = subtotal * taxRate;
  const total = subtotal + taxAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) { setError('Please enter your name'); return; }
    if (!form.customer_phone.trim()) { setError('Please enter your phone number'); return; }
    if (!form.customer_email.trim()) { setError('Please enter your email'); return; }
    if (orderType === 'dine_in' && !form.table_number.trim()) { setError('Please enter your table number'); return; }
    setPlacing(true); setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/orders/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          items: cart.map(i => ({ item_id: i.item_id, name: i.name, price: i.price, quantity: i.qty, spice_choice: i.spice_choice || '' })),
          total_amount: subtotal,
          order_type: orderType,
          table_number: orderType === 'dine_in' ? form.table_number : '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to place order');
      onSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 20, maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>📝 Checkout</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <div style={{ background: 'var(--primary-light)', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
            <h4 style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>🧾 Order Summary</h4>
            {cart.map(item => (
              <div key={item.item_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>{item.qty}× {item.name}{item.spice_choice ? ` (${item.spice_choice})` : ''}</span>
                <span style={{ fontWeight: 600 }}>${(item.price * item.qty).toFixed(2)}</span>
              </div>
            ))}
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
                <span>Subtotal (before tax)</span>
                <span style={{ color: 'var(--primary)' }}>${subtotal.toFixed(2)}</span>
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#166534' }}>
                ℹ️ Taxes & fees are calculated by the restaurant based on your local tax rate and will be applied at pickup.
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Full Name *</label>
              <input type="text" className="form-input" placeholder="John Doe" value={form.customer_name} onChange={e => setForm(p => ({ ...p, customer_name: e.target.value }))} required />
            </div>
              <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Phone Number *</label>
              <input type="tel" className="form-input" placeholder="+1 (555) 000-0000"
                value={form.customer_phone}
                onChange={e => {
                  const val = e.target.value.replace(/[^\d+\-\s()]/g, '');
                  setForm(p => ({ ...p, customer_phone: val }));
                }}
                onBlur={e => {
                  const digits = e.target.value.replace(/\D/g, '');
                  if (digits.length < 10) setError('Please enter a valid phone number (min 10 digits)');
                  else setError('');
                }}
                maxLength={15}
                required />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Email Address *</label>
              <input type="email" className="form-input" placeholder="you@example.com"
                value={form.customer_email}
                onChange={e => setForm(p => ({ ...p, customer_email: e.target.value }))}
                onBlur={e => {
                  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value);
                  if (!valid && e.target.value) setError('Please enter a valid email address');
                  else setError('');
                }}
                required />
            </div>
              {orderType === 'dine_in' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Table Number *</label>
                <input className="form-input" placeholder="e.g. Table 5" value={form.table_number} onChange={e => setForm(p => ({ ...p, table_number: e.target.value }))} required />
              </div>
            )}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Special Instructions (Optional)</label>
              <textarea className="form-textarea" placeholder="Any special requests, allergies, or notes..." value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} />
            </div>
            {error && (
              <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 14 }}>
                ❌ {error}
              </div>
            )}
            <button type="submit" disabled={placing} style={{ width: '100%', padding: '14px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: placing ? 'not-allowed' : 'pointer', opacity: placing ? 0.7 : 1 }}>
              {placing ? 'Placing Order...' : `✅ Place Order — $${subtotal.toFixed(2)} + tax`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Order Success Modal ──────────────────────────────────────────────
function OrderSuccessModal({ order, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 20, maxWidth: 400, width: '100%', padding: '40px 32px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
        <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Order Placed!</h2>
        <p style={{ color: '#666', marginBottom: 16 }}>Your order has been received. We'll start preparing it shortly!</p>
        <div style={{ background: 'var(--primary-light)', borderRadius: 12, padding: '14px 20px', marginBottom: 24 }}>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>Order Number</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>
            #{order.order_number || order.order_id?.slice(-4).toUpperCase()}
          </div>
        </div>
        <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
          A confirmation will be sent to <strong>{order.customer_email}</strong>
        </p>
        <button onClick={onClose} style={{ width: '100%', padding: '12px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
          Back to Menu
        </button>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────
function formatTime(time) {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m} ${ampm}`;
}

function formatSlots(h) {
  if (!h.is_open) return 'Closed';
  if (h.slots && h.slots.length > 0) return h.slots.map(s => `${formatTime(s.open)} – ${formatTime(s.close)}`).join('  |  ');
  if (h.open && h.close) return `${formatTime(h.open)} – ${formatTime(h.close)}`;
  return 'Closed';
}

function checkIsOpen(h) {
  if (!h || !h.is_open) return false;
  const now = new Date();
  const currentTime = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  if (h.slots && h.slots.length > 0) return h.slots.some(s => currentTime >= s.open && currentTime <= s.close);
  if (h.open && h.close) return currentTime >= h.open && currentTime <= h.close;
  return false;
}

function OpeningHours({ hours }) {
  const [showPopup, setShowPopup] = useState(false);
  if (!hours || hours.length === 0) return null;
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const todayHours = hours.find(h => h.day === today);
  const open = checkIsOpen(todayHours);
  return (
    <>
      <button onClick={() => setShowPopup(true)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'white', border: '1.5px solid var(--border)',
        borderRadius: 20, padding: '6px 14px', cursor: 'pointer',
        fontSize: 13, fontWeight: 600, marginBottom: 0
      }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: open ? '#22c55e' : '#ef4444', display: 'inline-block', flexShrink: 0 }} />
        <span style={{ color: open ? '#16a34a' : '#dc2626' }}>{open ? 'Open Now' : 'Closed Now'}</span>
        <span style={{ color: '#999', fontSize: 12 }}>· Hours ▾</span>
      </button>
      {showPopup && (
        <div onClick={() => setShowPopup(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, padding: '20px 0 32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px 16px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>🕐 Opening Hours</span>
              <button onClick={() => setShowPopup(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: 28, height: 28, fontSize: 14, cursor: 'pointer', color: '#666' }}>✕</button>
            </div>
            <div style={{ padding: '8px 0' }}>
              {hours.map(h => (
                <div key={h.day} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', background: h.day === today ? 'var(--primary-light)' : 'transparent' }}>
                  <span style={{ fontWeight: h.day === today ? 700 : 500, fontSize: 14, minWidth: 110, color: h.day === today ? 'var(--primary)' : '#333' }}>{h.day}</span>
                  <span style={{ fontSize: 13, color: h.is_open ? '#444' : '#999', fontWeight: h.day === today ? 600 : 400 }}>{formatSlots(h)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Catering Modal ───────────────────────────────────────────────────
function CateringModal({ slug, restaurantName, onClose }) {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', event_date: '',
    guest_count: '', budget: '', menu_preferences: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.event_date || !form.guest_count) {
      setError('Please fill in all required fields'); return;
    }
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/catering/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to submit');
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 20, maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ background: 'linear-gradient(135deg, #d97706, #f59e0b)', padding: '20px 24px', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 28, marginBottom: 4 }}>🎪</div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'white' }}>Catering Request</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>{restaurantName}</p>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        <div style={{ padding: '24px' }}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
              <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Request Submitted!</h3>
              <p style={{ color: '#666', marginBottom: 8 }}>We've received your catering request and will contact you shortly.</p>
              <p style={{ fontSize: 13, color: '#888', marginBottom: 24 }}>A confirmation has been sent to <strong>{form.email}</strong></p>
              <button onClick={onClose} style={{ background: '#d97706', color: 'white', border: 'none', borderRadius: 12, padding: '12px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Close</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Full Name *</label>
                  <input className="form-input" placeholder="John Doe" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Phone *</label>
                  <input className="form-input" placeholder="+1 (555) 000-0000" value={form.phone} onChange={e => { const val = e.target.value.replace(/[^\d+\-\s()]/g, ''); if (val.length <= 15) setForm(p => ({ ...p, phone: val })); }} pattern="[\d\s\-\+\(\)]{7,15}" title="Enter a valid phone number" required />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Email *</label>
                <input className="form-input" type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Event Date *</label>
                  <input className="form-input" type="date" value={form.event_date} onChange={e => setForm(p => ({ ...p, event_date: e.target.value }))} required min={new Date().toISOString().split('T')[0]} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Guest Count *</label>
                  <input className="form-input" type="number" placeholder="50" min="1" value={form.guest_count} onChange={e => setForm(p => ({ ...p, guest_count: e.target.value }))} required />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Budget (Optional)</label>
                <input className="form-input" placeholder="e.g. $500 - $1000" value={form.budget} onChange={e => setForm(p => ({ ...p, budget: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Menu Preferences (Optional)</label>
                <input className="form-input" placeholder="e.g. Vegetarian options, Biryani, Kebabs..." value={form.menu_preferences} onChange={e => setForm(p => ({ ...p, menu_preferences: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Additional Notes (Optional)</label>
                <textarea className="form-textarea" placeholder="Any special requests or additional information..." rows={3} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
              {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 14 }}>❌ {error}</div>}
              <button type="submit" disabled={submitting} style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #d97706, #f59e0b)', color: 'white', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                {submitting ? 'Submitting...' : '🎪 Submit Catering Request'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Check if restaurant is currently open ────────────────────────────
function isRestaurantOpen(opening_hours) {
  if (!opening_hours || opening_hours.length === 0) return true;
  const now = new Date();
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const todayName = days[now.getDay()];
  const todayHours = opening_hours.find(h => h.day === todayName);
  if (!todayHours || !todayHours.is_open) return false;
  const slots = todayHours.slots || [];
  const nowMins = now.getHours() * 60 + now.getMinutes();
  return slots.some(slot => {
    const [oh, om] = (slot.open || '00:00').split(':').map(Number);
    const [ch, cm] = (slot.close || '23:59').split(':').map(Number);
    const openMins = oh * 60 + om;
    const closeMins = ch * 60 + cm;
    return nowMins >= openMins && nowMins <= closeMins;
  });
}

function getNextOpenTime(opening_hours) {
  if (!opening_hours || opening_hours.length === 0) return null;
  const now = new Date();
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  for (let i = 0; i < 7; i++) {
    const checkDate = new Date(now);
    checkDate.setDate(now.getDate() + i);
    const dayName = days[checkDate.getDay()];
    const dayHours = opening_hours.find(h => h.day === dayName);
    if (!dayHours || !dayHours.is_open) continue;
    const slots = dayHours.slots || [];
    for (const slot of slots) {
      const [oh, om] = (slot.open || '00:00').split(':').map(Number);
      const openMins = oh * 60 + om;
      const nowMins = now.getHours() * 60 + now.getMinutes();
      if (i === 0 && openMins <= nowMins) continue;
      const h12 = oh % 12 || 12;
      const ampm = oh < 12 ? 'AM' : 'PM';
      const timeStr = `${h12}:${om.toString().padStart(2,'0')} ${ampm}`;
      const shortDay = dayName.slice(0, 3);
      return i === 0 ? `today at ${timeStr}` : `${shortDay} at ${timeStr}`;
    }
  }
  return null;
}

// ── Main Component ───────────────────────────────────────────────────
export default function PublicMenu() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [gallery, setGallery] = useState([]);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [selectedItem, setSelectedItem] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [onlineOrderMode, setOnlineOrderMode] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedLang, setSelectedLang] = useState('en');
  const [translating, setTranslating] = useState(false);
  const [translatedData, setTranslatedData] = useState(null);
  const translationCache = useRef({});
  const [showCatering, setShowCatering] = useState(false);
  const [showOrderPopup, setShowOrderPopup] = useState(false);
  const [orderType, setOrderType] = useState('online');
  const [heroIndex, setHeroIndex] = useState(0);
  const [spiceModal, setSpiceModal] = useState(null);
  const [selectedSpice, setSelectedSpice] = useState('');

  // ── Reaction popup state ────────────────────────────────────────────
  const [showReaction, setShowReaction] = useState(false);
  const [reactionSubmitted, setReactionSubmitted] = useState(false);
  const scrolledRef = useRef(false);

  // ── CHANGE 1: New refs and state for sticky category bar ───────────
  const toolbarRef = useRef(null);
  const categoryBarRef = useRef(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const [activeCategory, setActiveCategory] = useState(null);

  // ── CHANGE 2: Measure toolbar height dynamically ───────────────────
  useEffect(() => {
    const measure = () => {
      if (toolbarRef.current) {
        setToolbarHeight(toolbarRef.current.getBoundingClientRect().height);
      }
    };
    // Measure immediately
    measure();
    // Measure after fonts/images load
    window.addEventListener('load', measure);
    // Keep watching for resize
    const observer = new ResizeObserver(measure);
    if (toolbarRef.current) observer.observe(toolbarRef.current);
    return () => {
      window.removeEventListener('load', measure);
      observer.disconnect();
    };
  }, []);

  // ── Re-measure toolbar after data loads (spice row appears after data) ──
  useEffect(() => {
    if (!data || !toolbarRef.current) return;
    const timer = setTimeout(() => {
      if (toolbarRef.current) {
        setToolbarHeight(toolbarRef.current.getBoundingClientRect().height);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [data]);

  // ── CHANGE 3: Scroll spy — highlight active category pill ──────────
  useEffect(() => {
    if (!data) return;
    const handleScroll = () => {
      const offset = toolbarHeight + 60;
      // Check most-ordered section first
      const mostOrderedEl = document.getElementById('cat-most-ordered');
      if (mostOrderedEl) {
        const rect = mostOrderedEl.getBoundingClientRect();
        if (rect.top <= offset && rect.bottom > offset) {
          setActiveCategory('most-ordered');
          return;
        }
      }
      const cats = translatedData?.categories || (data?.categories || []);
      for (const cat of cats) {
        const el = document.getElementById('cat-' + cat.category_id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= offset && rect.bottom > offset) {
            setActiveCategory(cat.category_id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [data, translatedData, toolbarHeight]);

  // ── Auto-scroll active pill into view in the pill row ─────────────
  useEffect(() => {
    if (!activeCategory || !categoryBarRef.current) return;
    const activeBtn = categoryBarRef.current.querySelector(`[data-cat="${activeCategory}"]`);
    if (activeBtn) activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeCategory]);

  // ── Reaction timer ─────────────────────────────────────────────────
  useEffect(() => {
    try { if (sessionStorage.getItem('qrdm_reaction_done')) return; } catch(e) {}
    let showTimer;
    const onScroll = () => { if (window.scrollY > 100) scrolledRef.current = true; };
    window.addEventListener('scroll', onScroll, { passive: true });
    const show = () => setShowReaction(true);
    showTimer = setTimeout(() => {
      if (scrolledRef.current) {
        show();
      } else {
        const waitScroll = () => { show(); window.removeEventListener('scroll', waitScroll); };
        window.addEventListener('scroll', waitScroll, { once: true, passive: true });
      }
    }, 30000);
    return () => {
      clearTimeout(showTimer);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const [scanLimitReached, setScanLimitReached] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/menu/${slug}`)
      .then(async r => {
        if (r.status === 403) {
          const d = await r.json();
          if (d.error === 'scan_limit_reached') {
            setScanLimitReached(true);
            return null;
          }
        }
        return r.json();
      })
      .then(d => {
        if (!d) return;
        if (d.detail) throw new Error(d.detail);
        setData(d);
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/gallery/${slug}`)
          .then(r => r.json()).then(setGallery).catch(() => {});
          })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    }, [slug]);

  useEffect(() => {
    if (gallery.length < 2) return;
    const startIndex = gallery.findIndex(img => img.is_cover);
    setHeroIndex(startIndex >= 0 ? startIndex : 0);
    const interval = setInterval(() => {
      setHeroIndex(prev => (prev + 1) % gallery.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [gallery]);


  const addToCartDirect = (item, spiceChoice = '') => {
    const cartItem = spiceChoice ? { ...item, spice_choice: spiceChoice } : item;
    setCart(prev => {
      const existing = prev.find(i => i.item_id === item.item_id && i.spice_choice === spiceChoice);
      if (existing) return prev.map(i => i.item_id === item.item_id && i.spice_choice === spiceChoice ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { ...cartItem, qty: 1 }];
    });
  };

  const addToCart = (item) => {
    if ((item.spice_max || 0) > 0) {
      setSelectedSpice('');
      setSpiceModal(item);
      return;
    }
    addToCartDirect(item);
  };

  const handleLanguageChange = useCallback(async (lang) => {
    setSelectedLang(lang);
    setSearch('');
    if (lang === 'en' || !data) { setTranslatedData(null); return; }
    if (translationCache.current[lang]) { setTranslatedData(translationCache.current[lang]); return; }
    setTranslating(true);
    try {
      const { categories, items } = data;
      const catNames = categories.map(c => c.name);
      const itemNames = items.map(i => i.name);
      const itemDescs = items.map(i => i.description || '');
      const allTexts = [...catNames, ...itemNames, ...itemDescs];
      const translated = await translateBatch(allTexts, lang);
      const tCatNames = translated.slice(0, catNames.length);
      const tItemNames = translated.slice(catNames.length, catNames.length + itemNames.length);
      const tItemDescs = translated.slice(catNames.length + itemNames.length);
      const result = {
        categories: categories.map((c, i) => ({ ...c, name: tCatNames[i] || c.name })),
        items: items.map((item, i) => ({ ...item, name: tItemNames[i] || item.name, description: tItemDescs[i] || item.description })),
      };
      translationCache.current[lang] = result;
      setTranslatedData(result);
    } catch {
      setTranslatedData(null);
    } finally {
      setTranslating(false);
    }
  }, [data]);

  const updateQty = (itemId, newQty) => {
    if (newQty <= 0) setCart(prev => prev.filter(i => i.item_id !== itemId));
    else setCart(prev => prev.map(i => i.item_id === itemId ? { ...i, qty: newQty } : i));
  };

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });

  

  if (loading) return <div className="loading-center min-h-screen"><div className="loading-spinner"></div></div>;
  if (scanLimitReached) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', padding: 24 }}>
      <div style={{ background: 'white', borderRadius: 24, padding: '48px 32px', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>📵</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', marginBottom: 12 }}>Menu Temporarily Unavailable</h2>
        <p style={{ fontSize: 15, color: '#666', lineHeight: 1.6, marginBottom: 24 }}>
          This restaurant's free menu has reached its daily view limit. The menu will be available again tomorrow.
        </p>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 20px', marginBottom: 28 }}>
          <p style={{ fontSize: 13, color: '#166534', margin: 0, lineHeight: 1.6 }}>
            🍽️ <strong>Restaurant owner?</strong> Upgrade to a paid plan for unlimited menu views and online ordering.
          </p>
        </div>
        <a href="https://getqrdigimenu.com" target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-block', background: '#1a5c2a', color: 'white', padding: '14px 32px', borderRadius: 12, fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>
          View Plans at QR Digi Menu
        </a>
        <p style={{ fontSize: 12, color: '#aaa', marginTop: 20 }}>Powered by QR Digi Menu</p>
      </div>
    </div>
  );
  if (error) return <div className="empty-state min-h-screen"><div className="empty-state-icon">😕</div><h3>Menu Not Found</h3><p>{error}</p></div>;

  const { restaurant, categories: rawCategories, items: rawItems } = data;
  const categories = translatedData?.categories || rawCategories;
  const items = translatedData?.items || rawItems;
  const ui = UI[selectedLang] || UI.en;
  const taxInfo = detectTaxRate(restaurant.city || '');
  const taxRate = taxInfo.rate;
  const taxState = taxInfo.abbr;
  const isPro = (restaurant.plan || 'basic') === 'pro';
  const isOpen = isRestaurantOpen(restaurant.opening_hours);
  const nextOpen = getNextOpenTime(restaurant.opening_hours);

  const submitReaction = async (type) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/reactions/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reaction: type })
      });
    } catch(e) {}
    setReactionSubmitted(true);
    try { sessionStorage.setItem('qrdm_reaction_done', '1'); } catch(e) {}
    setTimeout(() => setShowReaction(false), 1500);
  };

  const filters = ['all', 'veg', 'non-veg', 'vegan', 'halal'];
  const filterLabels = { all: ui.all, veg: ui.veg, 'non-veg': ui.nonveg, vegan: ui.vegan, halal: ui.halal };
  // ── Most Ordered: items marked as popular by the restaurant owner ──
  const popularItems = items.filter(i => i.is_popular && i.is_available !== false && (filter === 'all' || i.dietary_type === filter));

  const filtered = (filter === 'all' ? items : items.filter(i => i.dietary_type === filter))
    .filter(i => !search.trim() || i.name.toLowerCase().includes(search.toLowerCase()) || (i.description && i.description.toLowerCase().includes(search.toLowerCase())));

  const allSpiceMaxes = items.map(i => i.spice_max || i.spice_level || 0).filter(v => v > 0);
  const allSpiceMins  = items.map(i => i.spice_min || (i.spice_level > 0 ? i.spice_level : 0)).filter(v => v > 0);
  const restaurantMax = allSpiceMaxes.length > 0 ? Math.max(...allSpiceMaxes) : 0;
  const restaurantMin = allSpiceMins.length  > 0 ? Math.min(...allSpiceMins)  : 0;
  const coverIndex = gallery.findIndex(img => img.is_cover);
  const coverImg = coverIndex >= 0 ? gallery[coverIndex] : gallery[0];
  

  return (
    <div style={{ paddingBottom: cartCount > 0 && onlineOrderMode ? 90 : 0 }}>

      {/* Hero */}
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        {coverImg || restaurant.cover_image_url ? (
          <div style={{ width: '100%', height: 280, position: 'relative', overflow: 'hidden' }}>
                        {gallery.length > 0 ? gallery.map((img, idx) => (
              <img key={img.image_url} src={img.image_url} alt={restaurant.name}
                onClick={() => openLightbox(idx)}
                style={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%',
                  objectFit: 'cover', display: 'block', cursor: 'pointer',
                  opacity: idx === heroIndex ? 1 : 0,
                  transition: 'opacity 1s ease-in-out',
                  pointerEvents: idx === heroIndex ? 'auto' : 'none',
                }}
                onError={e => e.target.style.display = 'none'} />
            )) : (
              <img src={restaurant.cover_image_url} alt={restaurant.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={e => e.target.style.display = 'none'} />
            )}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.65))', pointerEvents: 'none' }} />
            {gallery.length > 1 && (
              <div onClick={() => openLightbox(0)} style={{ position: 'absolute', bottom: 80, right: 16, background: 'rgba(0,0,0,0.6)', color: 'white', padding: '6px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer', backdropFilter: 'blur(4px)' }}>📷 {gallery.length} Photos</div>
            )}
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px 24px', color: 'white', textAlign: 'center', pointerEvents: 'none' }}>
              <h1 style={{ fontSize: 'clamp(22px, 5vw, 38px)', fontWeight: 700, fontFamily: "'Playfair Display', serif", marginBottom: 6, textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>{restaurant.name}</h1>
              {restaurant.description && <p style={{ fontSize: 14, opacity: 0.9, marginBottom: 8, maxWidth: 500, margin: '0 auto 8px' }}>{restaurant.description}</p>}
              <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                {restaurant.phone && <span>📞 {restaurant.phone}</span>}
                {restaurant.address && (
                  <span>
                    📍 {restaurant.address}
                    {restaurant.maps_link && (
                      <a href={restaurant.maps_link} target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ marginLeft: 6, background: 'rgba(255,255,255,0.25)', color: 'white', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, textDecoration: 'none', verticalAlign: 'middle', pointerEvents: 'auto' }}>
                        🗺️ Maps
                      </a>
                    )}
                  </span>
                )}
                {restaurant.cuisine_type && <span style={{ background: 'rgba(255,255,255,0.25)', padding: '2px 12px', borderRadius: 20 }}>{restaurant.cuisine_type}</span>}
              </div>
              {(restaurant.instagram_url || restaurant.facebook_url) && (
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 10 }}>
                  {restaurant.instagram_url && (
                    <a href={restaurant.instagram_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                      style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, textDecoration: 'none', backdropFilter: 'blur(4px)', pointerEvents: 'auto' }}>
                      📸 Instagram
                    </a>
                  )}
                  {restaurant.facebook_url && (
                    <a href={restaurant.facebook_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                      style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, textDecoration: 'none', backdropFilter: 'blur(4px)', pointerEvents: 'auto' }}>
                      👥 Facebook
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="public-menu-hero">
            <h1>{restaurant.name}</h1>
            {restaurant.description && <p>{restaurant.description}</p>}
            <div className="public-menu-info">
              {restaurant.phone && <span>📞 {restaurant.phone}</span>}
              {restaurant.address && (
                <span>
                  📍 {restaurant.address}
                  {restaurant.maps_link && (
                    <a href={restaurant.maps_link} target="_blank" rel="noopener noreferrer"
                      style={{ marginLeft: 6, background: 'rgba(255,255,255,0.25)', color: 'white', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, textDecoration: 'none', verticalAlign: 'middle', pointerEvents: 'auto' }}>
                      🗺️ Maps
                    </a>
                  )}
                </span>
              )}
              {restaurant.cuisine_type && <span className="badge badge-veg" style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>{restaurant.cuisine_type}</span>}
            </div>
            {(restaurant.instagram_url || restaurant.facebook_url) && (
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 10 }}>
                {restaurant.instagram_url && (
                  <a href={restaurant.instagram_url} target="_blank" rel="noopener noreferrer"
                    style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                    📸 Instagram
                  </a>
                )}
                {restaurant.facebook_url && (
                  <a href={restaurant.facebook_url} target="_blank" rel="noopener noreferrer"
                    style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                    👥 Facebook
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── CHANGE 4: Sticky toolbar with ref ── */}
      <div ref={toolbarRef} style={{ position: 'sticky', top: 0, zIndex: 100, background: 'white', borderBottom: '1px solid var(--border)', padding: '10px 16px' }}>
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#999' }}>🔍</span>
          <input
            type="text"
            placeholder={ui.search}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '9px 36px 9px 36px', borderRadius: 10, border: '1.5px solid var(--border)', fontSize: 14, outline: 'none', background: 'white', boxSizing: 'border-box' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#999' }}>✕</button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ width: '100%', padding: '8px 26px 8px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 13, fontWeight: 600, color: '#555', background: 'white', appearance: 'none', cursor: 'pointer', outline: 'none' }}
            >
              <option value="all">🍽️ All Items</option>
              <option value="veg">🟢 Veg</option>
              <option value="non-veg">🔴 Non Veg</option>
              <option value="vegan">🌿 Vegan</option>
            </select>
            <span style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#999', pointerEvents: 'none' }}>▾</span>
          </div>
          {isPro && (
            isOpen ? (
                <button
                onClick={() => { if (onlineOrderMode) { setOnlineOrderMode(false); setCart([]); setShowCart(false); } else { setShowOrderPopup(true); } }}
                style={{ padding: '6px 8px', whiteSpace: 'nowrap', background: onlineOrderMode ? 'var(--primary)' : 'white', color: onlineOrderMode ? 'white' : 'var(--primary)', border: '1.5px solid var(--primary)', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
              >
                {onlineOrderMode ? ('✅ ' + (orderType === 'dine_in' ? 'Dine-in' : 'Online') + (cartCount > 0 ? ' · ' + cartCount : '')) : '🛒 Order'}
              </button>
            ) : (
              <div style={{ padding: '8px 11px', whiteSpace: 'nowrap', background: '#fee2e2', color: '#991b1b', border: '2px solid #fca5a5', borderRadius: 8, fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                🔴 Closed
                {nextOpen && <span style={{ fontWeight: 400, fontSize: 11 }}>· Opens {nextOpen}</span>}
              </div>
            )
          )}
          {isPro && (
          <div style={{ position: 'relative', flex: '0 0 auto' }}>
            <span style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 12, pointerEvents: 'none', zIndex: 1 }}>🌐</span>
            <select
              value={selectedLang}
              onChange={e => handleLanguageChange(e.target.value)}
              style={{ padding: '4px 14px 4px 22px', borderRadius: 8, border: selectedLang !== 'en' ? '1.5px solid #1a5c2a' : '1px solid #e5e7eb', background: selectedLang !== 'en' ? '#f0fdf4' : 'white', color: selectedLang !== 'en' ? '#1a5c2a' : '#555', fontSize: 11, fontWeight: 500, cursor: 'pointer', appearance: 'none', outline: 'none', maxWidth: 100 }}
            >
              {LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.native !== lang.label ? `${lang.label} - ${lang.native}` : lang.label}
                </option>
              ))}
            </select>
            <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#999', pointerEvents: 'none' }}>▾</span>
          </div>
          )}
          {translating && (
            <span style={{ fontSize: 12, color: '#1a5c2a', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, border: '2px solid #1a5c2a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Translating...
            </span>
          )}
          {!translating && items.some(i => (i.spice_max || i.spice_level || 0) > 0) && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: 'white', border: '0.5px solid #e5e5e5', borderRadius: 8, flexShrink: 0 }}>
              <span style={{ fontSize: 11 }}>🌶️</span>
              <span style={{ fontSize: 11, color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>Spicy 1–{restaurantMax}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── CHANGE 5: Category bar — dynamic top + active highlight + scroll spy ── */}
      {categories.length > 1 && (
        <div
          ref={categoryBarRef}
          style={{ display: 'flex', gap: 8, padding: '8px 16px', overflowX: 'auto', background: 'white', borderBottom: '1px solid var(--border)', position: 'sticky', top: toolbarHeight, zIndex: 99 }}
        >
          {/* Most Ordered pill — only show if restaurant has popular items */}
          {popularItems.length > 0 && (
            <button
              data-cat="most-ordered"
              onClick={() => {
                const el = document.getElementById('cat-most-ordered');
                if (el) {
                  const offset = toolbarHeight + (categoryBarRef.current?.offsetHeight || 0);
                  const top = el.getBoundingClientRect().top + window.scrollY - offset - 8;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
                setActiveCategory('most-ordered');
              }}
              style={{
                padding: '5px 14px',
                borderRadius: 20,
                whiteSpace: 'nowrap',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                border: activeCategory === 'most-ordered' ? '1.5px solid #f97316' : '1.5px solid #fed7aa',
                background: activeCategory === 'most-ordered' ? '#f97316' : '#fff7ed',
                color: activeCategory === 'most-ordered' ? 'white' : '#c2410c',
                flexShrink: 0,
                transition: 'all 0.2s',
              }}
            >
              🔥 Most Ordered
            </button>
          )}
          {categories.map(cat => (
            <button
              key={cat.category_id}
              data-cat={cat.category_id}
              onClick={() => {
                const el = document.getElementById('cat-' + cat.category_id);
                if (el) {
                  const offset = toolbarHeight + (categoryBarRef.current?.offsetHeight || 0);
                  const top = el.getBoundingClientRect().top + window.scrollY - offset - 8;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
                setActiveCategory(cat.category_id);
              }}
              style={{
                padding: '5px 14px',
                borderRadius: 20,
                whiteSpace: 'nowrap',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                border: activeCategory === cat.category_id
                  ? '1.5px solid var(--primary)'
                  : '1.5px solid var(--border)',
                background: activeCategory === cat.category_id ? 'var(--primary)' : 'white',
                color: activeCategory === cat.category_id ? 'white' : '#555',
                flexShrink: 0,
                transition: 'all 0.2s',
              }}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      <div className="public-menu-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 0 }}>
          <OpeningHours hours={restaurant.opening_hours} />
          
          {restaurant.plan === 'pro' && (
            <button onClick={() => setShowCatering(true)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'linear-gradient(135deg, #d97706, #f59e0b)',
              border: 'none', borderRadius: 20, padding: '6px 14px',
              cursor: 'pointer', fontSize: 13, fontWeight: 700,
              color: 'white', marginBottom: 4
            }}>
              🎪 Catering
            </button>
          )}
        </div>

        {/* ── Most Ordered Section — shows first if restaurant has popular items ── */}
        {popularItems.length > 0 && !search.trim() && (
          <div id="cat-most-ordered" style={{ marginBottom: 28 }}>
            <div style={{ marginBottom: 14 }}>
              <h2 style={{
                fontSize: 18, fontWeight: 700, color: '#c2410c',
                paddingBottom: 8, borderBottom: '2px solid #f97316',
                display: 'inline-block', margin: 0,
                display: 'flex', alignItems: 'center', gap: 8
              }}>
                🔥 Most Ordered
              </h2>
              <p style={{ fontSize: 13, color: '#9a3412', marginTop: 6, marginBottom: 0 }}>
                Our customers' favourite picks
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
              {popularItems.map(item => {
                const inCart = cart.find(c => c.item_id === item.item_id);
                const isAvailable = item.is_available !== false;
                const itemMin = item.spice_min || (item.spice_level > 0 ? item.spice_level : 0);
                const itemMax = item.spice_max || item.spice_level || 0;
                const hasSpice = itemMax > 0;
                const isRange = itemMin !== itemMax && itemMin > 0;
                const spiceLabel = hasSpice ? (isRange ? itemMin + '–' + itemMax : itemMax + '/' + restaurantMax) : '';
                const dietBg = item.dietary_type === 'veg' ? '#e8f5e9' : item.dietary_type === 'vegan' ? '#f0fdf4' : '#fde8e8';
                const dietColor = item.dietary_type === 'veg' ? '#1a5c2a' : item.dietary_type === 'vegan' ? '#166534' : '#c0392b';
                const dietDot = item.dietary_type === 'veg' ? '#1a5c2a' : item.dietary_type === 'vegan' ? '#22c55e' : '#ef4444';
                return (
                  <div key={'popular-' + item.item_id} style={{ background: 'white', borderRadius: 14, border: '1.5px solid #fed7aa', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    {/* 🔥 badge */}
                    <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, background: '#f97316', color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>
                      🔥 Popular
                    </div>
                    <div style={{ position: 'relative', cursor: item.image_url ? 'pointer' : 'default' }} onClick={() => item.image_url && isAvailable && setSelectedItem(item)}>
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display = 'none'} />
                      ) : (
                        <div style={{ width: '100%', height: 140, background: 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <span style={{ fontSize: 40 }}>🔥</span>
                          <span style={{ fontSize: 11, color: '#c2410c', fontWeight: 600 }}>Popular Item</span>
                        </div>
                      )}
                      {item.dietary_type && item.dietary_type !== '' && item.dietary_type !== 'none' && (
                        <div style={{ position: 'absolute', top: 8, right: 8, background: dietBg, border: '1.5px solid ' + dietDot, borderRadius: 20, padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: dietColor }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: dietDot, display: 'inline-block' }} />
                          {item.dietary_type}
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '10px 12px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f1117', lineHeight: 1.3, flex: 1 }}>{item.name}</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--primary)', flexShrink: 0 }}>${Number(item.price).toFixed(2)}</div>
                      </div>
                      {item.description && (
                        <div style={{ fontSize: 11, color: '#888', lineHeight: 1.5, marginBottom: 8, overflow: 'hidden', maxHeight: '2.8em' }}>{item.description}</div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap', overflow: 'hidden' }}>
                          {hasSpice && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <div style={{ display: 'flex', gap: 2 }}>
                                {Array.from({ length: restaurantMax }, (_, i) => {
                                  const level = i + 1;
                                  const inRange = level >= itemMin && level <= itemMax;
                                  const hues = ['#fbbf24','#f59e0b','#f97316','#ef4444','#dc2626','#b91c1c','#7f1d1d'];
                                  return <div key={i} style={{ width: 7, height: 7, borderRadius: 2, background: inRange ? (hues[i] || '#991b1b') : '#e5e5e5' }} />;
                                })}
                              </div>
                              <span style={{ fontSize: 10, color: '#b91c1c', fontWeight: 700 }}>{spiceLabel}</span>
                            </div>
                          )}
                          {item.portion_size && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#555', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{item.portion_size}</span>
                          )}
                          {item.serves && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#555', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{item.serves}</span>
                          )}
                        </div>
                        {onlineOrderMode && isAvailable && isOpen && (
                          inCart ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <button onClick={() => updateQty(item.item_id, inCart.qty - 1)} style={{ width: 26, height: 26, borderRadius: '50%', border: '1.5px solid var(--primary)', background: 'white', color: 'var(--primary)', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>−</button>
                              <span style={{ fontWeight: 700, minWidth: 16, textAlign: 'center', fontSize: 14, color: 'var(--primary)' }}>{inCart.qty}</span>
                              <button onClick={() => updateQty(item.item_id, inCart.qty + 1)} style={{ width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'var(--primary)', color: 'white', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>+</button>
                            </div>
                          ) : (
                            <button onClick={() => addToCart(item)} style={{ background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+ Add</button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {categories.map(cat => {
          const catItems = filtered.filter(i => i.category_id === cat.category_id);
          if (!catItems.length) return null;
          return (
            <div key={cat.category_id} id={'cat-' + cat.category_id} style={{ marginBottom: 28 }}>
              <div style={{ marginBottom: 14 }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f1117', paddingBottom: 8, borderBottom: '2px solid var(--primary)', display: 'inline-block', margin: 0 }}>{cat.name}</h2>
                {cat.description && (
                  <p style={{ fontSize: 13, color: '#6b7280', marginTop: 6, marginBottom: 0 }}>📌 {cat.description}</p>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                {catItems.map(item => {
                  const inCart = cart.find(c => c.item_id === item.item_id);
                  const isAvailable = item.is_available !== false;
                  const itemMin = item.spice_min || (item.spice_level > 0 ? item.spice_level : 0);
                  const itemMax = item.spice_max || item.spice_level || 0;
                  const hasSpice = itemMax > 0;
                  const isRange  = itemMin !== itemMax && itemMin > 0;
                  const spiceLabel = hasSpice ? (isRange ? itemMin + '–' + itemMax : itemMax + '/' + restaurantMax) : '';
                  const barTotal = restaurantMax - restaurantMin + 1;
                  const barLeft  = hasSpice ? ((itemMin - restaurantMin) / barTotal) * 100 : 0;
                  const barWidth = hasSpice ? ((itemMax - itemMin + 1) / barTotal) * 100 : 0;
                  const dietBg = item.dietary_type === 'veg' ? '#e8f5e9' : item.dietary_type === 'vegan' ? '#f0fdf4' : '#fde8e8';
                  const dietColor = item.dietary_type === 'veg' ? '#1a5c2a' : item.dietary_type === 'vegan' ? '#166534' : '#c0392b';
                  const dietDot = item.dietary_type === 'veg' ? '#1a5c2a' : item.dietary_type === 'vegan' ? '#22c55e' : '#ef4444';
                  return (
                    <div key={item.item_id} style={{ background: 'white', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden', opacity: isAvailable ? 1 : 0.55, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ position: 'relative', cursor: item.image_url ? 'pointer' : 'default' }} onClick={() => item.image_url && isAvailable && setSelectedItem(item)}>
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name} style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display = 'none'} />
                        ) : (
                          <div style={{ width: '100%', height: 140, background: 'linear-gradient(135deg, #f5f0ea 0%, #ede8e0 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}><div style={{ width: 48, height: 48, borderRadius: '50%', background: '#ddd6cc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 3C7.03 3 3 7.03 3 12s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9zm0 16c-3.86 0-7-3.14-7-7s3.14-7 7-7 7 3.14 7 7-3.14 7-7 7z" fill="#b5a99a"/><path d="M8.5 9.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S10.83 11 10 11s-1.5-.67-1.5-1.5zM14 9.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S16.33 11 15.5 11 14 10.33 14 9.5zM12 17c-2.33 0-4.31-1.46-5.11-3.5h10.22c-.8 2.04-2.78 3.5-5.11 3.5z" fill="#b5a99a"/></svg></div><span style={{ fontSize: 11, color: '#b5a99a', fontWeight: 500 }}>No image</span></div>
                        )}
                        {item.dietary_type && item.dietary_type !== '' && item.dietary_type !== 'none' && (
                          <div style={{ position: 'absolute', top: 8, right: 8, background: dietBg, border: '1.5px solid ' + dietDot, borderRadius: 20, padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: dietColor }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: dietDot, display: 'inline-block' }} />
                            {item.dietary_type}
                          </div>
                        )}
                        {!isAvailable && (
                          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.38)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ background: 'rgba(0,0,0,0.72)', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>Unavailable today</span>
                          </div>
                        )}
                      </div>
                      <div style={{ padding: '10px 12px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f1117', lineHeight: 1.3, flex: 1 }}>{item.name}</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--primary)', flexShrink: 0 }}>${Number(item.price).toFixed(2)}</div>
                        </div>
                        {item.description && (
                          <div style={{ fontSize: 11, color: '#888', lineHeight: 1.5, marginBottom: 8, overflow: 'hidden', maxHeight: '2.8em' }}>{item.description}</div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap', overflow: 'hidden' }}>
                            {hasSpice && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <div style={{ display: 'flex', gap: 2 }}>
                                  {Array.from({ length: restaurantMax }, (_, i) => {
                                    const level = i + 1;
                                    const inRange = level >= itemMin && level <= itemMax;
                                    const hues = ['#fbbf24','#f59e0b','#f97316','#ef4444','#dc2626','#b91c1c','#7f1d1d'];
                                    return <div key={i} style={{ width: 7, height: 7, borderRadius: 2, background: inRange ? (hues[i] || '#991b1b') : '#e5e5e5' }} />;
                                  })}
                                </div>
                                <span style={{ fontSize: 10, color: '#b91c1c', fontWeight: 700 }}>{spiceLabel}</span>
                              </div>
                            )}
                            {item.portion_size && (
                              <span style={{ fontSize: 10, fontWeight: 700, color: '#555', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{item.portion_size}</span>
                            )}
                            {item.serves && (
                              <span style={{ fontSize: 10, fontWeight: 700, color: '#555', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{item.serves}</span>
                            )}
                          </div>
                          {onlineOrderMode && isAvailable && isOpen && (
                            inCart ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <button onClick={() => updateQty(item.item_id, inCart.qty - 1)} style={{ width: 26, height: 26, borderRadius: '50%', border: '1.5px solid var(--primary)', background: 'white', color: 'var(--primary)', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>−</button>
                                <span style={{ fontWeight: 700, minWidth: 16, textAlign: 'center', fontSize: 14, color: 'var(--primary)' }}>{inCart.qty}</span>
                                <button onClick={() => updateQty(item.item_id, inCart.qty + 1)} style={{ width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'var(--primary)', color: 'white', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>+</button>
                              </div>
                            ) : (
                              <button onClick={() => addToCart(item)} style={{ background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+ Add</button>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">🍽️</div>
            <h3>{ui.noItems}</h3>
          </div>
        )}
      </div>

      {/* Floating Cart Button */}
      {cartCount > 0 && onlineOrderMode && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, width: 'calc(100% - 32px)', maxWidth: 480 }}>
          <button onClick={() => setShowCart(true)} style={{ width: '100%', padding: '14px 20px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 16, fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}>
            <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 20, padding: '2px 10px', fontSize: 14 }}>{cartCount} item{cartCount > 1 ? 's' : ''}</span>
            <span>🛒 {ui.viewCart}</span>
            <span>${cartTotal.toFixed(2)}</span>
          </button>
        </div>
      )}

      {/* Modals */}
      {lightbox.open && gallery.length > 0 && <GalleryLightbox images={gallery} startIndex={lightbox.index} onClose={closeLightbox} />}
      {selectedItem && <ItemLightbox item={selectedItem} onClose={() => setSelectedItem(null)} onAddToCart={addToCart} />}
      {showCart && onlineOrderMode && <CartSidebar cart={cart} onClose={() => setShowCart(false)} onUpdateQty={updateQty} onRemove={(id) => setCart(prev => prev.filter(i => i.item_id !== id))} onCheckout={() => { setShowCart(false); setShowCheckout(true); }} restaurantName={restaurant.name} taxRate={taxRate} taxState={taxState} />}
      {showCheckout && <CheckoutModal cart={cart} slug={slug} orderType={orderType} onClose={() => setShowCheckout(false)} onSuccess={(order) => { setShowCheckout(false); setCart([]); setOrderSuccess(order); }} taxRate={taxRate} taxState={taxState} />}
      {orderSuccess && <OrderSuccessModal order={orderSuccess} onClose={() => setOrderSuccess(null)} />}
      {showCatering && <CateringModal slug={slug} restaurantName={restaurant.name} onClose={() => setShowCatering(false)} />}
      {showOrderPopup && (
        <div onClick={() => setShowOrderPopup(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, padding: '24px 20px 40px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <span style={{ fontWeight: 700, fontSize: 18 }}>How would you like to order?</span>
              <button onClick={() => setShowOrderPopup(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: 30, height: 30, fontSize: 14, cursor: 'pointer', color: '#666' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <button onClick={() => { setShowOrderPopup(false); setOrderType('dine_in'); setOnlineOrderMode(true); }} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px', background: '#f0fdf4', border: '2px solid #86efac', borderRadius: 16, cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontSize: 32 }}>🍽️</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#166534' }}>Dine-in Order</div>
                  <div style={{ fontSize: 13, color: '#4b7a5a', marginTop: 2 }}>Order from your table</div>
                </div>
              </button>
                <button onClick={() => { setShowOrderPopup(false); setOrderType('online'); setOnlineOrderMode(true); }} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px', background: '#eff6ff', border: '2px solid #93c5fd', borderRadius: 16, cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontSize: 32 }}>📦</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#1e40af' }}>Online Order</div>
                  <div style={{ fontSize: 13, color: '#3b5fc0', marginTop: 2 }}>Delivery / Takeaway</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reaction Popup */}
      {showReaction && (
        <div style={{ position: 'fixed', bottom: 20, right: 16, zIndex: 9999, background: 'white', borderRadius: 16, padding: '14px 16px', boxShadow: '0 4px 24px rgba(0,0,0,0.15)', width: 220, animation: 'slideInRight 0.3s cubic-bezier(0.34,1.56,0.64,1)', border: '1px solid #f0f0f0' }}>
          <style>{`
            @keyframes slideInRight { from { transform: translateX(120%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
            @keyframes popIn { 0% { transform: scale(0.8); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
          `}</style>
          {!reactionSubmitted ? (
            <>
              <button onClick={() => { setShowReaction(false); try { sessionStorage.setItem('qrdm_reaction_done','1'); } catch(e) {} }} style={{ position:'absolute', top:8, right:10, background:'none', border:'none', fontSize:16, color:'#ccc', cursor:'pointer', lineHeight:1 }}>✕</button>
              <p style={{ fontSize:12, fontWeight:700, color:'#1a1a1a', marginBottom:2, paddingRight:16 }}>How was your menu experience?</p>
              <p style={{ fontSize:11, color:'#aaa', marginBottom:10 }}>Rate QR Digi Menu</p>
              <div style={{ display:'flex', gap:6, justifyContent:'space-between' }}>
                {[
                  { emoji:'❤️', label:'Love', value:'love' },
                  { emoji:'👍', label:'Like',  value:'good' },
                  { emoji:'😊', label:'Good',  value:'okay' },
                ].map(r => (
                  <button key={r.value} onClick={() => submitReaction(r.value)} style={{ flex: 1, background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '8px 4px', cursor: 'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:3, transition:'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background='#f0fdf4'; e.currentTarget.style.borderColor='#4ade80'; }}
                    onMouseLeave={e => { e.currentTarget.style.background='#f9fafb'; e.currentTarget.style.borderColor='#e5e7eb'; }}
                  >
                    <span style={{ fontSize:22 }}>{r.emoji}</span>
                    <span style={{ fontSize:10, fontWeight:600, color:'#555' }}>{r.label}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div style={{ textAlign:'center', padding:'6px 0', animation:'popIn 0.3s ease' }}>
              <div style={{ fontSize:28, marginBottom:4 }}>🎉</div>
              <p style={{ fontSize:13, fontWeight:700, color:'#1a5c2a', margin:0 }}>Thank you!</p>
            </div>
          )}
        </div>
      )}

              {/* ── Spice Level Modal ── */}
      {spiceModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 9999, padding: 0 }}>
          <div style={{ background: 'white', borderRadius: '16px 16px 0 0', padding: '16px 16px 24px', width: '100%', maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>🌶️ Spice level</span>
                <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{spiceModal.name}</div>
              </div>
              <button onClick={() => { setSpiceModal(null); setSelectedSpice(''); }}
                style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: 24, height: 24, fontSize: 12, cursor: 'pointer', color: '#666' }}>✕</button>
            </div>
            <div style={{ height: 1, background: '#f3f4f6', margin: '10px 0' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              {[
                { label: 'Mild', emoji: '😊', desc: 'No heat' },
                { label: 'Medium', emoji: '😏', desc: 'Light heat' },
                { label: 'Spicy', emoji: '🌶️', desc: 'Good kick' },
                { label: 'Extra Hot', emoji: '🔥', desc: 'Very spicy' },
              ].map(({ label, emoji, desc }) => (
                <button key={label} onClick={() => setSelectedSpice(label)} style={{
                  padding: '8px 6px', borderRadius: 10,
                  border: `2px solid ${selectedSpice === label ? '#1a5c2a' : '#e5e7eb'}`,
                  background: selectedSpice === label ? '#f0fdf4' : '#fafafa',
                  cursor: 'pointer', textAlign: 'center'
                }}>
                  <div style={{ fontSize: 18, marginBottom: 2 }}>{emoji}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: selectedSpice === label ? '#1a5c2a' : '#1a1a1a' }}>{label}</div>
                  <div style={{ fontSize: 10, color: '#999' }}>{desc}</div>
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                if (!selectedSpice) return;
                addToCartDirect(spiceModal, selectedSpice);
                setSpiceModal(null);
                setSelectedSpice('');
              }}
              disabled={!selectedSpice}
              style={{ width: '100%', padding: '11px', background: selectedSpice ? '#1a5c2a' : '#e5e7eb', color: selectedSpice ? 'white' : '#999', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: selectedSpice ? 'pointer' : 'not-allowed' }}
            >
              {selectedSpice ? `Add to Cart — ${selectedSpice}` : 'Select spice level'}
            </button>
          </div>
        </div>
      )}

      {/* Powered by footer */}
      <div style={{ textAlign: 'center', padding: '24px 16px 32px', borderTop: '1px solid var(--border)', marginTop: 8 }}>
        <div style={{ marginBottom: 12 }}>
          <a href="/privacy" style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'none', margin: '0 8px' }}
            onMouseEnter={e => e.currentTarget.style.color = '#6b7280'}
            onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
          >Privacy Policy</a>
          <span style={{ color: '#d1d5db', fontSize: 11 }}>·</span>
          <a href="/terms" style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'none', margin: '0 8px' }}
            onMouseEnter={e => e.currentTarget.style.color = '#6b7280'}
            onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
          >Terms of Service</a>
        </div>
        <a href="https://getqrdigimenu.com" target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', color: '#9ca3af', fontSize: 12, fontWeight: 500, letterSpacing: '0.01em', transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = '#6b7280'}
          onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
            <rect x="3" y="3" width="8" height="8" rx="1.5" stroke="#9ca3af" strokeWidth="2"/>
            <rect x="13" y="3" width="8" height="8" rx="1.5" stroke="#9ca3af" strokeWidth="2"/>
            <rect x="3" y="13" width="8" height="8" rx="1.5" stroke="#9ca3af" strokeWidth="2"/>
            <rect x="13" y="13" width="3" height="3" rx="0.5" stroke="#9ca3af" strokeWidth="2"/>
            <rect x="18" y="13" width="3" height="3" rx="0.5" stroke="#9ca3af" strokeWidth="2"/>
            <rect x="13" y="18" width="3" height="3" rx="0.5" stroke="#9ca3af" strokeWidth="2"/>
            <rect x="18" y="18" width="3" height="3" rx="0.5" stroke="#9ca3af" strokeWidth="2"/>
          </svg>
          Powered by <img src={require('../assets/logo.png')} alt="QR Digi Menu" style={{ width: 16, height: 16, borderRadius: 3, objectFit: 'cover', marginLeft: 4, marginRight: 3, verticalAlign: 'middle' }} /><strong style={{ color: '#6b7280' }}>QR Digi Menu</strong>
        </a>
      </div>
    </div>
  );
}